import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthenticationContext } from "components/context/AuthenticationProvider";
import { useCustomModel } from "components/context/CustomProvider";
import useDidValueChange from "components/hooks/useDidValueChanged";
import {
  baseUrl,
  retryOnError,
  sendCacheDiscardEvent,
} from "components/shared/apiCrudBuilder";
import { useContext, useMemo } from "react";

export const getCacheKey = ({ modelName, ...props }) => {
  const keyName = `RingBack-${modelName}`;
  if (Object.values(props).filter(Boolean).length == 0) return [keyName];
  return [
    keyName,
    ...Object.values(props)
      .filter(Boolean)
      .map((v) => v.toString()),
  ];
};

const useCustomDataSource = (modelName) => {
  const model = useCustomModel(modelName);
  const { appAxiosRingback } = useContext(AuthenticationContext);
  const queryClient = useQueryClient();

  if (!model) return null;

  const modelSlug = model.modelName
    .match(/[A-Z]+(?![a-z])|[A-Z][a-z]*/g)
    .map((p) => p.toLowerCase())
    .join("-");
  const path = `${baseUrl}/${modelSlug}`;

  const getRows = async ({ filterModel, sortModel, start = 0, end = 10 }) => {
    const defaultFilter = {};
    const filterModelCombined = {
      ...filterModel,
      ...defaultFilter,
      items: [...(filterModel?.items || []), ...(defaultFilter?.items || [])],
    };
    const params = {
      filterModel: JSON.stringify(filterModelCombined || {}),
      sortModel: JSON.stringify(sortModel || []),
      start,
      end,
    };
    const res = await appAxiosRingback.get(`${path}/data-source`, { params });
    res.data.rows.forEach((item) => {
      queryClient.setQueryData(getCacheKey({ modelName, id: item.id }), item, {
        staleTime: 10000,
        cacheTime: 10000,
      });
    });
    return res.data;
  };

  const useOne = (id, options) => {
    return useQuery({
      queryKey: getCacheKey({ modelName, id }),
      queryFn: () =>
        appAxiosRingback.get(`${path}/${id}/`).then((res) => res.data),
      retry: retryOnError,
      staleTime: 10000,
      cacheTime: 10000,
      ...options,
    });
  };

  const { mutate: updateOne, mutateAsync: updateOneAsync } = useMutation({
    mutationFn: ({ id, data }) => {
      return appAxiosRingback.put(`${path}/${id}/`, data).then((res) => {
        queryClient.setQueryData(getCacheKey({ modelName, id }), res.data, {
          staleTime: 10000,
          cacheTime: 10000,
        });
        return res.data;
      });
    },
    onSuccess: (_, data) => {
      sendCacheDiscardEvent(
        getCacheKey({ modelName, id: data.id }),
        "updateOne",
      );
      model.extraCacheKeys &&
        model.extraCacheKeys(data).forEach((key) => {
          queryClient.invalidateQueries(key);
        });
    },
  });

  const { mutate: createOne, mutateAsync: createOneAsync } = useMutation({
    mutationFn: ({ data }) => {
      return appAxiosRingback.post(`${path}/`, data).then((res) => {
        queryClient.setQueryData(
          getCacheKey({ modelName, id: res.data.id }),
          res.data,
          {
            staleTime: 10000,
            cacheTime: 10000,
          },
        );
        return res.data;
      });
    },
    onSuccess: (data) => {
      sendCacheDiscardEvent(
        getCacheKey({ modelName, id: data.id }),
        "createOne",
      );
      model.extraCacheKeys &&
        model.extraCacheKeys(data)?.forEach((key) => {
          queryClient.invalidateQueries(key);
        });
    },
  });

  const { mutate: deleteOne } = useMutation({
    mutationFn: (id) => {
      return appAxiosRingback.delete(`${path}/${id}/`).then((res) => {
        queryClient.removeQueries({ queryKey: getCacheKey({ modelName, id }) });
        return res.data;
      });
    },
    onSuccess: (_, id) => {
      sendCacheDiscardEvent(getCacheKey({ modelName, id }), "deleteOne");
    },
  });

  const actions = Object.fromEntries(
    (model.extraActions || []).map((action) => {
      const { name, label, hookName, type, url, extraCacheKeys } = action;
      if (type === "query") {
        return [
          hookName,
          (params, options) => {
            const { dealId: id, ...restParams } = params;
            return useQuery({
              queryKey: getCacheKey({ modelName, id, ...restParams }),
              queryFn: () => {
                const fullUrl = url({ ...params });
                return appAxiosRingback
                  .get(`${path}/${fullUrl}`)
                  .then((res) => res.data);
              },
              retry: retryOnError,
              staleTime: 10000,
              cacheTime: 10000,
              ...options,
            });
          },
        ];
      } else if (type === "mutation") {
        return [
          hookName,
          (params, options) => {
            return useMutation({
              mutationFn: (data) => {
                const fullUrl = url({ ...params, ...data });
                return appAxiosRingback
                  .post(`${path}/${fullUrl}`)
                  .then((res) => res.data);
              },
              onSuccess: (_, data) => {
                extraCacheKeys &&
                  extraCacheKeys({ ...params, ...data }).forEach((key) => {
                    queryClient.invalidateQueries(key);
                  });
              },
              ...options,
            });
          },
        ];
      } else {
        throw new Error(`Unsupported action type: ${type}`);
      }
    }),
  );

  useDidValueChange(model, "model");

  return useMemo(() => {
    return {
      getRows,
      useOne,
      updateOne,
      updateOneAsync,
      createOne,
      createOneAsync,
      deleteOne,
      key: getCacheKey({ modelName })[0],
      model,
      ...actions,
    };
  }, [getRows, useOne, updateOne, createOne, deleteOne, modelName, model]);
};

export default useCustomDataSource;
